<template>
  <div
    :id="`${source}add_to_list_dropdown_content`"
    class="flex flex-col"
    :class="width"
  >
    <div v-if="entityLists?.length > 0" class="flex flex-col max-h-[244px]">
      <div
        class="flex flex-col py-3 px-2"
        :class="{ border_bottom: filteredEntityLists.length > 0 }"
      >
        <span
          class="secondary_color proxima_nova font-semibold text-xs leading-[17px] px-3 pb-2"
        >
          CHOOSE A LIST
        </span>
        <CustomInput
          format="text"
          v-model="searchQuery"
          placeholder="Search here"
          type="primary"
          :icon="imageNames.SEARCH_VARIANT1"
          alt="search_icon"
          auto-focus
        />
      </div>

      <div
        v-if="filteredEntityLists?.length"
        class="flex flex-col p-2 overflow-y-scroll custom_scrollbar"
      >
        <div
          v-for="(list, index) in filteredEntityLists"
          :key="`${list?.id}_list_${index}`"
          :class="[
            'dropdown_item flex-shrink-0 flex items-center flex-row gap-2 rounded-lg',
            { selected: selectMode && selectedItems?.includes(list?.id) },
          ]"
          :ref="(el) => setItemRef(el, list.id + '_' + index)"
          @click="handleItemClick(list?.id)"
        >
          <a-tooltip
            :key="`${list?.id}_list_${index}`"
            placement="top"
            :title="
              shouldShowTooltip(list.id + '_' + index)
                ? list?.list_name || ''
                : ''
            "
          >
            <div class="flex flex-row w-full items-center gap-1 text-sm">
              <div
                class="truncate"
                :ref="(el) => setContainerRef(el, list.id + '_' + index)"
              >
                <span
                  :ref="(el) => setTextRef(el, list.id + '_' + index)"
                  class="text_color truncate text-sm font-normal leading-[17px]"
                  >{{ list.list_name }}
                </span>
              </div>
              <ThemeImage
                v-if="list?.private_flag"
                :icon="imageNames.LOCK_GREY"
                alt="private_icon"
                class="size-[14px]"
              />
            </div>
          </a-tooltip>
        </div>
      </div>
    </div>
    <div
      v-if="
        source !== entityTypes.EMAIL &&
        source !== entityTypes.SEQUENCE_EMAILS &&
        source !== entityTypes.LINKEDIN_LOG &&
        source !== entityTypes.SEQUENCE_LINKEDIN_LOGS
      "
      :class="['flex flex-col p-2', { border_top: entityLists?.length > 0 }]"
    >
      <div
        class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
        @click="$emit('create-new-list')"
      >
        + Create New List
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import CustomInput from "@/src/components/CustomInput.vue";
import { useTruncationTooltip } from "@/src/composables/useTruncationTooltip.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants";

const { setTextRef, setContainerRef, setItemRef, shouldShowTooltip } =
  useTruncationTooltip();

const props = defineProps({
  source: { type: String, required: true },
  entityLists: { type: Array, default: () => [] },
  selectMode: { type: Boolean, default: true },
  selectedItems: { type: Array, default: () => [] },
  width: { type: String, default: 'w-[266px]' },
});

const emit = defineEmits(["item-click", "create-new-list", "reset-search"]);

const searchQuery = ref("");
const filteredEntityLists = ref([...props.entityLists]);

const handleItemClick = (id) => {
  if (!id) return;
  emit("item-click", id);
};

const handleSearch = (query = "") => {
  if (!Array.isArray(props.entityLists) || !props.entityLists.length) {
    filteredEntityLists.value = [];
    return;
  }

  const searchTerm = query.trim().toLowerCase();
  if (!searchTerm) {
    filteredEntityLists.value = [...props.entityLists];
    return;
  }

  filteredEntityLists.value = props.entityLists.filter((list) =>
    list?.list_name?.toLowerCase()?.includes(searchTerm),
  );
};

const resetSearchState = () => {
  searchQuery.value = "";
  filteredEntityLists.value = Array.isArray(props.entityLists)
    ? [...props.entityLists]
    : [];
  emit("reset-search");
};

watch(searchQuery, (newValue) => {
  handleSearch(newValue);
});

watch(
  () => props.entityLists,
  (newValue) => {
    filteredEntityLists.value = Array.isArray(newValue) ? [...newValue] : [];
  },
  { immediate: true },
);

defineExpose({
  resetSearchState,
});
</script>

<style scoped>
.selected {
  background-color: var(--white-opacity-8);
}
</style>
